/**
 * Blog ↔ Tool Eşleşme — Matematiksel Skor + Spam Guard + Özgün CTA
 * =================================================================
 *
 * Mimari (Sprint blog-tool-ctr-enterprise, 2026-05-25):
 *  1. Tool DNA: manifest.topics[] + manifest.intent + manifest.spamCapacity
 *  2. Blog DNA: slug + başlık'tan otomatik topic extraction
 *  3. Skor: 0.55 × Jaccard(topics) + 0.25 × Mevzuat overlap + 0.20 × Intent bonus
 *  4. Spam guard: tool capacity'i geçince yeni eşleşme reddedilir
 *  5. Özgün CTA: her tool için template; blog title harmanlanır
 *
 * Geriye uyum: getRelatedToolsForBlog(slug) ToolManifest[] döner — eski
 * imza korunur. Yeni özellikler getRelatedToolsWithCta + getMatchDebug.
 *
 * Spam algılaması: Google "exact match" repeating anchor'ı penalize eder.
 * Tek tool 17 blog'a generic CTA ile linklendi → kaldırıldı. Maximum 6 tool
 * + spam capacity per tool.
 */
import type { ToolManifest } from '@/shared/tools/types';
import { getToolBySlug, PUBLIC_TOOLS } from '@/shared/tools/registry';
import { MODUL_PUBLIC_URL } from '@/shared/seo/agacData';

// ─── Konfigürasyon ──────────────────────────────────────────────────────────

/**
 * Eşleşme eşiği — bu skorun altındaki tool'lar blog altında gösterilmez.
 * Düşük tutulursa irrelevant tool görünür; yüksek tutulursa boş kalır.
 * 0.30 = orta-zayıf bağ, 0.50 = güçlü bağ.
 */
const MATCH_THRESHOLD = 0.30;

/** Bir blog altında gösterilecek tool sayısı (cognitive load + UX) */
const MAX_TOOLS_PER_BLOG = 3;

/** Skor ağırlıkları (toplam 1.0) */
const WEIGHT_TOPIC = 0.55;
const WEIGHT_MEVZUAT = 0.25;
const WEIGHT_INTENT = 0.20;

// ─── Blog DNA çıkarma ────────────────────────────────────────────────────────

/**
 * Blog slug + başlık'tan topic keyword'leri çıkarır.
 * Static map (eski substring pattern'in DNA versiyonu). Yeni blog eklenince
 * burada DNA tanımlanmazsa fallback default kullanılır.
 *
 * Manuel tanım çünkü Türkçe lemmatizer yok; slug yapısı zaten kebab-case.
 */
const BLOG_DNA: Record<string, { topics: string[]; mevzuat: string[]; intent: 'kavram' | 'mevzuat' | 'uygulama' | 'karsilastirma' | 'rehber' }> = {
  // Risk metodolojileri
  'fine-kinney-risk-analizi-yontemi': {
    topics: ['fine-kinney', 'risk-analizi', 'olasilik', 'frekans', 'siddet', 'risk-puanlama', 'risk-degerlendirmesi'],
    mevzuat: ['6331', '28512'],
    intent: 'rehber',
  },
  '5x5-risk-matrisi-nedir-nasil-hesaplanir': {
    topics: ['5x5-matris', 'risk-matrisi', 'olasilik', 'siddet', 'risk-analizi', 'risk-degerlendirmesi', 'l-tipi'],
    mevzuat: ['6331', '28512'],
    intent: 'rehber',
  },
  'fine-kinney-vs-5x5-risk-matrisi-karsilastirma': {
    topics: ['fine-kinney', '5x5-matris', 'risk-analizi', 'risk-degerlendirmesi', 'karsilastirma'],
    mevzuat: ['6331', '28512'],
    intent: 'karsilastirma',
  },
  'risk-degerlendirmesi-nasil-yapilir-adim-adim': {
    topics: ['risk-degerlendirmesi', 'risk-analizi', 'tehlike', 'olasilik', 'siddet'],
    mevzuat: ['6331', '28512'],
    intent: 'rehber',
  },
  'risk-degerlendirmesinde-siddet-degisir-mi': {
    topics: ['risk-degerlendirmesi', 'siddet', 'risk-puanlama'],
    mevzuat: ['6331', '28512'],
    intent: 'kavram',
  },
  'yapay-zeka-ile-risk-analizi-nasil-yapilir': {
    topics: ['risk-analizi', 'yapay-zeka', 'ai', 'fine-kinney', '5x5-matris'],
    mevzuat: ['6331'],
    intent: 'uygulama',
  },

  // NACE & tehlike sınıfı
  'nace-kodu-nedir-tehlike-sinifi-nasil-belirlenir': {
    topics: ['nace', 'nace-kodu', 'tehlike-sinifi', 'sektor', 'kod-sorgulama'],
    mevzuat: ['6331', '33211'],
    intent: 'rehber',
  },
  'tehlike-sinifi-degisiklikleri-2026-nace-listesi': {
    topics: ['nace', 'tehlike-sinifi', 'tehlikeli', 'cok-tehlikeli', 'az-tehlikeli', 'isyeri-tehlike'],
    mevzuat: ['33211', '6331'],
    intent: 'mevzuat',
  },

  // Çalışan temsilcisi, İSG Kurulu, yıllık plan
  'calisan-temsilcisi-secimi-zorunlu-mu': {
    topics: ['calisan-temsilcisi', 'isg-kurulu', 'calisan-sayisi', 'isveren-yukumluluk', 'yasal-yukumluluk'],
    mevzuat: ['6331', '28750'],
    intent: 'mevzuat',
  },
  'isg-kurulu-nedir-toplanti-yonetimi-rehberi': {
    topics: ['isg-kurulu', 'toplanti', 'calisan-temsilcisi', 'isg-kurulu-uyeleri', 'yasal-yukumluluk'],
    mevzuat: ['6331', '28532'],
    intent: 'rehber',
  },
  'yillik-isg-egitim-plani-nasil-hazirlanir': {
    topics: ['yillik-plan', 'egitim-plani', 'egitim-tarihi', 'periyot', 'takvim', '33212'],
    mevzuat: ['33212', '6331'],
    intent: 'rehber',
  },
  'isg-egitim-yonetmeligi-2026-degisiklikleri': {
    topics: ['egitim-plani', 'egitim-suresi', '33212', 'tehlike-sinifi', 'periyot'],
    mevzuat: ['33212', '6331'],
    intent: 'mevzuat',
  },

  // İş kazası, kök neden, DF
  'is-kazasi-bildirimi-sgk-3-isgunu-rehberi': {
    topics: ['is-kazasi', 'sgk-bildirim', 'kaza-bildirim', 'isveren-yukumluluk'],
    mevzuat: ['6331', '14'],
    intent: 'mevzuat',
  },
  'duzeltici-onleyici-faaliyet-dof-nedir': {
    topics: ['dof', 'duzeltici-faaliyet', 'kok-neden', 'uygunsuzluk', 'iso-45001'],
    mevzuat: ['6331', '45001'],
    intent: 'rehber',
  },
  'kok-neden-analizi-yontemleri-5-whys-fishbone-fta': {
    topics: ['kok-neden', 'fishbone', 'ishikawa', '5-whys', 'fta', 'uygunsuzluk', 'is-kazasi'],
    mevzuat: ['45001'],
    intent: 'rehber',
  },

  // Acil durum, tatbikat
  'acil-durum-eylem-plani-madde-12-zorunlu-icerikler': {
    topics: ['acil-durum', 'eylem-plani', 'tatbikat', 'yangin', 'tahliye'],
    mevzuat: ['28681', '6331'],
    intent: 'mevzuat',
  },
  'acil-durum-tatbikati-yonetmelik-rehberi': {
    topics: ['acil-durum', 'tatbikat', 'yangin', 'tahliye', 'periyot', 'takvim'],
    mevzuat: ['28681', '6331'],
    intent: 'rehber',
  },

  // Periyodik kontrol, ekipman, İSG-KATİP
  'periyodik-kontrol-sozlesmesi-isg-katip-rehberi': {
    topics: ['periyodik-kontrol', 'is-ekipmani', 'isg-katip', 'sozlesme', 'kontrol-listesi'],
    mevzuat: ['28628', '6331'],
    intent: 'rehber',
  },
  'isg-katip-nedir-nasil-kullanilir': {
    topics: ['isg-katip', 'periyodik-kontrol', 'sozlesme', 'isg-uzmani', 'isveren-yukumluluk'],
    mevzuat: ['6331'],
    intent: 'rehber',
  },

  // ISO 45001 + mevzuat genel
  'iso-45001-nedir-isg-yonetim-sistemi-rehberi': {
    topics: ['iso-45001', 'yonetim-sistemi', 'pdca', 'risk-analizi', 'denetim'],
    mevzuat: ['45001', '6331'],
    intent: 'kavram',
  },
  'ai-veri-guvenligi-isg-yazilimi-riskmatik-yaklasimi': {
    topics: ['ai', 'yapay-zeka', 'veri-guvenligi', 'kvkk', 'isg-yazilimi'],
    mevzuat: ['kvkk', 'iso-27001'],
    intent: 'kavram',
  },
  'saha-gozlemde-yapay-zeka-fotograftan-bulgu-tespiti': {
    topics: ['saha-gozlem', 'yapay-zeka', 'ai', 'fotograf-analizi', 'bulgu-tespiti', '5x5-matris', 'kontrol-hiyerarsisi', 'kkd', 'iso-45001', 'risk-degerlendirmesi'],
    mevzuat: ['6331', 'iso-45001'],
    intent: 'uygulama',
  },
  'isg-yazilimi-secim-rehberi-osgb-icin-10-kriter': {
    topics: ['isg-yazilimi', 'osgb', 'yazilim-secimi', 'kriterler'],
    mevzuat: ['6331'],
    intent: 'rehber',
  },
  'is-guvenligi-uzmani-nasil-olunur-turkiye-dunya-karsilastirma': {
    topics: ['isg-uzmani', 'sertifika', 'meslek', 'karsilastirma'],
    mevzuat: ['6331'],
    intent: 'kavram',
  },

  // KKD blog (yeni eklendi 2026-05-25)
  'kkd-secim-rehberi-6-kategori-en-standart-tablosu': {
    topics: ['kkd', 'koruyucu-donanim', 'baret', 'gozluk', 'maske', 'eldiven', 'ayakkabi', 'en-397', 'en-388', 'en-iso-20345', 'kontrol-hiyerarsisi'],
    mevzuat: ['28695', '6331'],
    intent: 'rehber',
  },

  // Gürültü/Titreşim Maruziyet blog (yeni eklendi 2026-05-25)
  'gurultu-titresim-maruziyet-80-85-87-db-esik-tablosu': {
    topics: ['maruziyet', 'gurultu', 'titresim', 'desibel', 'lex-8h', 'a-8', 'el-kol-titresimi', 'butun-vucut-titresimi', 'esik-tablosu', 'akustik'],
    mevzuat: ['28721', '28743', '6331'],
    intent: 'rehber',
  },

  // LOTO/EKED Prosedür blog (yeni eklendi 2026-05-25)
  'loto-proseduru-enerji-izolasyon-6-adim-rg-28628': {
    topics: ['loto', 'eked', 'etiketle-kilitle', 'emniyete-al-dene', 'enerji-izolasyon', 'lockout', 'tagout', 'bakim-onarim', 'is-ekipmani', 'kilitleme', 'etiketleme'],
    mevzuat: ['28628', '6331'],
    intent: 'rehber',
  },
};

// ─── Jaccard benzerliği ─────────────────────────────────────────────────────

function jaccard(a: string[], b: string[]): number {
  if (a.length === 0 || b.length === 0) return 0;
  const setA = new Set(a);
  const setB = new Set(b);
  const intersection = [...setA].filter((x) => setB.has(x)).length;
  const union = new Set([...setA, ...setB]).size;
  return union === 0 ? 0 : intersection / union;
}

// ─── Intent uyum bonusu ─────────────────────────────────────────────────────

/**
 * Blog intent × Tool intent uyumu (0.0–1.0). Doğal eşleşmeler:
 * - rehber ↔ rehber/sorgu/hesap = 1.0
 * - mevzuat ↔ hesap/sorgu = 0.9 (uzman somut karşılık ister)
 * - karsilastirma ↔ hesap = 0.8
 * - kavram ↔ sorgu = 0.6 (sözlük ile uyum)
 * - uygulama ↔ form/hesap = 0.9
 */
function intentBonus(blogIntent: string, toolIntent: ToolManifest['intent']): number {
  const matrix: Record<string, Record<string, number>> = {
    rehber: { rehber: 1.0, sorgu: 0.8, hesap: 0.9, form: 0.7, takvim: 0.8 },
    mevzuat: { rehber: 0.7, sorgu: 0.9, hesap: 1.0, form: 0.7, takvim: 0.9 },
    karsilastirma: { rehber: 0.8, sorgu: 0.7, hesap: 0.9, form: 0.5, takvim: 0.6 },
    uygulama: { rehber: 0.8, sorgu: 0.6, hesap: 0.9, form: 1.0, takvim: 0.7 },
    kavram: { rehber: 0.5, sorgu: 1.0, hesap: 0.5, form: 0.4, takvim: 0.5 },
  };
  return matrix[blogIntent]?.[toolIntent] ?? 0.5;
}

// ─── Skor hesaplama ─────────────────────────────────────────────────────────

interface MatchScore {
  toolSlug: string;
  score: number;
  topicScore: number;
  mevzuatScore: number;
  intentScore: number;
}

function calculateMatchScores(blogSlug: string): MatchScore[] {
  const blogDna = BLOG_DNA[blogSlug];
  if (!blogDna) return [];

  return PUBLIC_TOOLS
    .filter((t) => t.enabled)
    .map((tool) => {
      const topicScore = jaccard(blogDna.topics, tool.topics);
      const mevzuatScore = jaccard(blogDna.mevzuat, (tool.mevzuat ?? []).map((m) => m.no));
      const intentScore = intentBonus(blogDna.intent, tool.intent);
      const score =
        WEIGHT_TOPIC * topicScore +
        WEIGHT_MEVZUAT * mevzuatScore +
        WEIGHT_INTENT * intentScore;
      return { toolSlug: tool.slug, score, topicScore, mevzuatScore, intentScore };
    })
    .sort((a, b) => b.score - a.score);
}

// ─── Spam guard ─────────────────────────────────────────────────────────────

/**
 * Tüm bloglara skor uygulanır, sonra her tool için en yüksek skorlu
 * `spamCapacity` blog'a izin verilir. Capacity dolan tool diğer bloglarda
 * gözükmez (spam koruması).
 */
function buildGlobalRanking(): Map<string, Set<string>> {
  const toolToBlogs = new Map<string, Set<string>>();

  // Her blog için skorlu eşleşmeleri biriktir
  const allMatches: Array<{ blogSlug: string; toolSlug: string; score: number }> = [];
  Object.keys(BLOG_DNA).forEach((blogSlug) => {
    const scores = calculateMatchScores(blogSlug);
    scores
      .filter((s) => s.score >= MATCH_THRESHOLD)
      .forEach((s) => allMatches.push({ blogSlug, toolSlug: s.toolSlug, score: s.score }));
  });

  // Her tool için en yüksek skorlu N blog'u (spamCapacity) seç
  PUBLIC_TOOLS.forEach((tool) => {
    const capacity = tool.spamCapacity ?? 5;
    const top = allMatches
      .filter((m) => m.toolSlug === tool.slug)
      .sort((a, b) => b.score - a.score)
      .slice(0, capacity);
    toolToBlogs.set(tool.slug, new Set(top.map((m) => m.blogSlug)));
  });

  return toolToBlogs;
}

let _cachedRanking: Map<string, Set<string>> | null = null;
function getGlobalRanking(): Map<string, Set<string>> {
  if (!_cachedRanking) _cachedRanking = buildGlobalRanking();
  return _cachedRanking;
}

// ─── Özgün CTA üretimi ──────────────────────────────────────────────────────

/**
 * Tool slug + Blog DNA'sına göre özgün, tool-spesifik CTA mesajı.
 * Generic "33 modüllü dijital sistem" kopyası yasak — uzman eylem-yönelimli mesaj.
 */
const CTA_TEMPLATES: Record<string, string> = {
  'nace-araci': 'Firmanızın NACE kodunu ve tehlike sınıfını ücretsiz sorgulayın — 2026 güncel veritabanı.',
  'kkd-secim-yardimcisi': 'Sektörünüze uygun KKD listesini EN/ISO standart kodlarıyla PDF olarak indirin.',
  'ekipman-kontrol-listesi': 'İş ekipmanınız için periyodik kontrol formunu hazır olarak alın — Yön. 28628 uyumlu.',
  'yasal-yukumluluk-hesaplayici': 'Çalışan sayınız + tehlike sınıfınızdan yola çıkarak yasal yükümlülüklerinizi hesaplayın.',
  'isg-takvim': 'İSG eğitim + tatbikat + periyodik kontrol takviminizi çalışan sayısına göre üretin.',
  'loto-proseduru': 'Enerji izolasyon (LOTO) prosedürünü makine + enerji tipine göre özelleştirin.',
};

function getCtaForTool(toolSlug: string): string {
  return CTA_TEMPLATES[toolSlug] ?? 'Bu aracı ücretsiz kullanın, kayıt gerektirmez.';
}

// ─── Public API ─────────────────────────────────────────────────────────────

export interface ToolMatchWithCta {
  tool: ToolManifest;
  cta: string;
  score: number;
}

/**
 * Blog slug'ına göre eşleşen tool'ları skor sırasıyla döner.
 * Spam guard uygulanır — her tool kendi capacity'i kadar blog'da görünür.
 */
export function getRelatedToolsForBlog(blogSlug: string | null | undefined): ToolManifest[] {
  if (!blogSlug) return [];
  return getRelatedToolsWithCta(blogSlug).map((m) => m.tool);
}

/**
 * Tool + özgün CTA + skor — frontend "İlgili Araçlar" bölümünde kullanılır.
 */
export function getRelatedToolsWithCta(blogSlug: string | null | undefined): ToolMatchWithCta[] {
  if (!blogSlug) return [];
  const slugLower = blogSlug.toLowerCase();
  if (!BLOG_DNA[slugLower]) return [];

  const ranking = getGlobalRanking();
  const scores = calculateMatchScores(slugLower);

  return scores
    .filter((s) => s.score >= MATCH_THRESHOLD)
    .filter((s) => ranking.get(s.toolSlug)?.has(slugLower) ?? false)
    .slice(0, MAX_TOOLS_PER_BLOG)
    .map((s) => {
      const tool = getToolBySlug(s.toolSlug);
      if (!tool) return null;
      return { tool, cta: getCtaForTool(s.toolSlug), score: s.score };
    })
    .filter((m): m is ToolMatchWithCta => m !== null);
}

/**
 * Debug — admin sayfasında her blog için tüm skorları + neden eşleştiğini gösterir.
 */
export interface MatchDebug {
  blogSlug: string;
  blogDna: typeof BLOG_DNA[string] | null;
  matches: Array<{
    toolSlug: string;
    score: number;
    topicScore: number;
    mevzuatScore: number;
    intentScore: number;
    passedThreshold: boolean;
    passedSpamGuard: boolean;
    finalShown: boolean;
  }>;
}

export function getMatchDebug(blogSlug: string): MatchDebug {
  const blogDna = BLOG_DNA[blogSlug] ?? null;
  const scores = calculateMatchScores(blogSlug);
  const ranking = getGlobalRanking();

  const matches = scores.map((s) => {
    const passedThreshold = s.score >= MATCH_THRESHOLD;
    const passedSpamGuard = ranking.get(s.toolSlug)?.has(blogSlug) ?? false;
    return {
      toolSlug: s.toolSlug,
      score: Number(s.score.toFixed(3)),
      topicScore: Number(s.topicScore.toFixed(3)),
      mevzuatScore: Number(s.mevzuatScore.toFixed(3)),
      intentScore: Number(s.intentScore.toFixed(3)),
      passedThreshold,
      passedSpamGuard,
      finalShown: passedThreshold && passedSpamGuard,
    };
  });

  return { blogSlug, blogDna, matches };
}

/** Yeni blog DNA'sı runtime'da eklemek için (admin paneli). */
export function registerBlogDna(slug: string, dna: typeof BLOG_DNA[string]): void {
  BLOG_DNA[slug] = dna;
  _cachedRanking = null; // cache invalidate
}

// ─── Blog → Hub / Pillar CTA (araç değil; örnek hub'ı veya ticari pillar) ────

/**
 * Belirli blog yazıları için en alakalı sayfaya doğrudan CTA.
 * Araç sistemiyle karışmaz; blog başlığı altında, makale içeriği üstünde gösterilir.
 * Hedef ya bir içerik hub'ı (örnekler) ya da ticari pillar sayfasıdır
 * (/isg-yazilimi, /iso-45001-yazilimi, /ai-destekli-risk-analizi). Yüksek ticari
 * niyetli blog → eşleşen pillar = iç-link equity + arama niyeti karşılama
 * (rakiplerin Ads verdiği head-term'lerde organik otorite akışı).
 */
export const BLOG_HUB_CTAS: Record<string, { href: string; label: string; description: string }> = {
  'yillik-isg-egitim-plani-nasil-hazirlanir': {
    href: '/yillik-egitim-plani/ornekler',
    label: 'Yıllık Eğitim Planı Örneklerini İncele',
    description: '30 farklı sektör için Resmî Gazete 33212 uyumlu hazır plan — ücretsiz PDF indirin; Excel üyelikte işletmenize özel.',
  },
  'isg-egitim-yonetmeligi-2026-degisiklikleri': {
    href: '/yillik-egitim-plani/ornekler',
    label: 'Hazır Eğitim Planı Örnekleri',
    description: '2026 yönetmeliği uyumlu 30 sektör örneği — ücretsiz PDF indirin; Excel üyelikte işletmenize özel.',
  },
  'fine-kinney-risk-analizi-yontemi': {
    href: '/risk-analizi/ornekler',
    label: 'Risk Analizi Örneklerini İncele',
    description: 'Fine-Kinney yöntemiyle hazırlanmış 30 sektör örneği — ücretsiz PDF indirin; Excel ve Word üyelikte işletmenize özel.',
  },
  '5x5-risk-matrisi-nedir-nasil-hesaplanir': {
    href: '/risk-analizi/ornekler',
    label: 'Risk Analizi Örneklerini İncele',
    description: '5×5 matris yöntemiyle hazırlanmış 30 sektör örneği — ücretsiz PDF indirin; Excel ve Word üyelikte işletmenize özel.',
  },
  'fine-kinney-vs-5x5-risk-matrisi-karsilastirma': {
    href: '/risk-analizi/ornekler',
    label: 'Her İki Yöntemle Hazırlanmış Örnekler',
    description: 'Fine-Kinney ve 5×5 yönteminde 30 sektör gerçek örneği — ücretsiz PDF indirin; Excel ve Word üyelikte işletmenize özel.',
  },
  'risk-degerlendirmesi-nasil-yapilir-adim-adim': {
    href: '/risk-analizi/ornekler',
    label: 'Hazır Risk Analizi Örnekleri',
    description: 'ISO 45001 uyumlu 30 sektör örneği, adım adım anlatımla hazırlanmış gerçek belgeler — ücretsiz PDF indirin; Excel ve Word üyelikte işletmenize özel.',
  },
  'risk-degerlendirmesinde-siddet-degisir-mi': {
    href: '/risk-analizi/ornekler',
    label: 'Risk Analizi Örneklerini İncele',
    description: 'Şiddet ve olasılık değerlendirmesi yapılmış gerçek sektör örnekleri — ücretsiz PDF indirin; Excel ve Word üyelikte işletmenize özel.',
  },
  'yapay-zeka-ile-risk-analizi-nasil-yapilir': {
    href: '/ai-destekli-risk-analizi',
    label: 'Yapay Zeka Destekli Risk Analizini İnceleyin',
    description: 'İnsan tetikler, yapay zeka üretir, sertifikalı uzman onaylar — hibrit yapay zeka risk analizini görün.',
  },
  'saha-gozlemde-yapay-zeka-fotograftan-bulgu-tespiti': {
    href: '/fotograftan-tehlike-tespiti',
    label: 'Fotoğraftan Tehlike Tespitini İnceleyin',
    description: 'Yapay zeka saha fotoğrafından tehlikeyi tespit edip düzeltici faaliyete bağlar — canlı aracı görün.',
  },
  // 'acil durum tatbikatı' blogu (drill niyeti) → en doğru hub = TATBİKAT hub'ı
  // (/acil-durum-plani değil; o hub, plan-niyetli acil-durum-eylem-plani blogunda kalıyor).
  // Yeni tatbikat hub'ı izoleydi → 3047-gösterimlik blogdan inbound equity + semantik eşleşme.
  'acil-durum-tatbikati-yonetmelik-rehberi': {
    href: '/tatbikat-plani/ornekler',
    label: 'Tatbikat Örneklerini İncele',
    description: 'Sektörel örnek acil durum tatbikatları — senaryo, timeline, katılım listesi ve değerlendirme raporlarıyla, PDF indir.',
  },
  'acil-durum-eylem-plani-madde-12-zorunlu-icerikler': {
    href: '/acil-durum-plani/ornekler',
    label: 'Acil Durum Planı Örneklerini İncele',
    description: 'Madde 12\'deki zorunlu içerikleri karşılayan sektörel örnek acil durum planları — PDF olarak indirin.',
  },
  // Yüksek ticari niyetli blog'lar → eşleşen pillar (hub değil). Bu blog'ların
  // şimdiye dek CTA'sı yoktu; arama niyeti doğrudan ürün/uyum sayfasına işaret eder.
  'iso-45001-nedir-isg-yonetim-sistemi-rehberi': {
    href: '/iso-45001-yazilimi',
    label: 'ISO 45001 Yazılımıyla Uyumu Sağlayın',
    description: 'Her kaydı ilgili ISO 45001 maddesine bağlayan, denetime hazır doküman yönetimini inceleyin.',
  },
  'isg-yazilimi-secim-rehberi-osgb-icin-10-kriter': {
    href: '/isg-yazilimi',
    label: 'Riskmatik İSG Yazılımını İnceleyin',
    description: 'Risk analizi, saha gözlem, eğitim ve denetim hazırlığı tek sistemde — 33 modül, ISO 45001 + 6331 uyumlu.',
  },
  'ai-veri-guvenligi-isg-yazilimi-riskmatik-yaklasimi': {
    href: '/ai-destekli-risk-analizi',
    label: 'Yapay Zeka Destekli Risk Analizini Görün',
    description: 'Uzman onaylı, denetim kanıt zincirli hibrit yapay zeka akışını inceleyin.',
  },

  // ── Ticari-komşu tamamlama (CORE §0 — her blog en yakın ticari sayfaya) ──
  // Bilgi → problem → çözüm → ilgili Riskmatik sayfası. Hedef = en yakın modül detay sayfası / hub / pillar.
  'nace-kodu-nedir-tehlike-sinifi-nasil-belirlenir': {
    href: '/risk-analizi/ornekler',
    label: 'Tehlike Sınıfınıza Uygun Risk Analizi Örnekleri',
    description: 'NACE tehlike sınıfınıza göre farklı sektörlerden hazırlanmış gerçek risk analizi örneklerini inceleyin — ücretsiz PDF indirin; Excel ve Word üyelikte işletmenize özel.',
  },
  'tehlike-sinifi-degisiklikleri-2026-nace-listesi': {
    href: '/risk-analizi/ornekler',
    label: 'Sektörünüze Uygun Risk Analizi Örnekleri',
    description: 'Tehlike sınıfınıza göre hazırlanmış sektör risk analizi örneklerini görün — ücretsiz PDF indirin; Excel ve Word üyelikte işletmenize özel.',
  },
  'calisan-temsilcisi-secimi-zorunlu-mu': {
    href: MODUL_PUBLIC_URL['calisan-temsilcisi'], // modül detay sayfası (SSoT agacData)
    label: 'Çalışan Temsilcisi Seçimini Dijital Yürütün',
    description: 'Seçim ve atama sürecini, resmî belgeleri ve öneri defterini Riskmatik Çalışan Temsilcisi modülünde yönetin.',
  },
  'isg-kurulu-nedir-toplanti-yonetimi-rehberi': {
    href: MODUL_PUBLIC_URL['isg-kurullari'], // modül detay sayfası (SSoT agacData)
    label: 'İSG Kurulu Toplantılarını Dijital Yönetin',
    description: 'Gündem, karar ve katılım kayıtlarını Riskmatik İSG Kurulu modülünde tek panelden yönetin.',
  },
  'is-kazasi-bildirimi-sgk-3-isgunu-rehberi': {
    href: MODUL_PUBLIC_URL['is-kazalari'], // modül detay sayfası (SSoT agacData)
    label: 'Kaza ve Düzeltici Faaliyetleri Uçtan Uca Takip Edin',
    description: 'İş kazası bildirimi, kök neden ve düzeltici faaliyet zincirini Riskmatik\'te tek sistemde kayıt altına alın.',
  },
  'duzeltici-onleyici-faaliyet-dof-nedir': {
    href: MODUL_PUBLIC_URL['duzeltici-faaliyetler'], // modül detay sayfası (SSoT agacData)
    label: 'Düzeltici Faaliyetleri Dijital Yönetin',
    description: 'Uygunsuzluk → kök neden → düzeltici faaliyet → kapanış zincirini Riskmatik\'te takip edin.',
  },
  'kok-neden-analizi-yontemleri-5-whys-fishbone-fta': {
    href: MODUL_PUBLIC_URL['duzeltici-faaliyetler'], // modül detay sayfası (SSoT agacData)
    label: 'Kök Neden Analizini Düzeltici Faaliyete Bağlayın',
    description: '5 Neden, Balık Kılçığı ve FTA çıktılarınızı Riskmatik\'te düzeltici faaliyet ve takip sistemine bağlayın.',
  },
  'periyodik-kontrol-sozlesmesi-isg-katip-rehberi': {
    href: '/isg-katip-eklentisi',
    label: 'İSG-KATİP İşlemlerini Hızlandırın',
    description: 'Periyodik kontrol ve İSG-KATİP sözleşme adımlarını Riskmatik Chrome eklentisiyle tek tıkla yapın.',
  },
  'isg-katip-nedir-nasil-kullanilir': {
    href: '/isg-katip-eklentisi',
    label: 'İSG-KATİP\'i Riskmatik Eklentisiyle Kullanın',
    description: 'İSG-KATİP giriş, atama ve sözleşme adımlarını Riskmatik Chrome eklentisiyle otomatikleştirin.',
  },
  'is-guvenligi-uzmani-nasil-olunur-turkiye-dunya-karsilastirma': {
    href: '/tek-uzman-icin',
    label: 'Bireysel İş Güvenliği Uzmanları için Riskmatik',
    description: '5-20 firma yöneten bağımsız iş güvenliği uzmanları için tasarlanmış Riskmatik çözümünü inceleyin.',
  },
  'kkd-secim-rehberi-6-kategori-en-standart-tablosu': {
    href: '/ai-destekli-risk-analizi',
    label: 'KKD\'yi Risk Analizinize Bağlayın',
    description: 'Riskmatik yapay zeka destekli risk analizinde tehlikeye uygun KKD\'yi kontrol hiyerarşisiyle önerir — inceleyin.',
  },
  'gurultu-titresim-maruziyet-80-85-87-db-esik-tablosu': {
    href: '/ai-destekli-risk-analizi',
    label: 'Maruziyeti Risk Değerlendirmesine Bağlayın',
    description: 'Gürültü ve titreşim maruziyet sonuçlarını Riskmatik\'in yapay zeka destekli risk analizine bağlayın.',
  },
  'loto-proseduru-enerji-izolasyon-6-adim-rg-28628': {
    href: '/isg-yazilimi',
    label: 'LOTO ve Bakım Güvenliğini Dijitalleştirin',
    description: 'Enerji izolasyon prosedürleri, ekipman ve yetkili personel takibini Riskmatik\'te tek sistemde yönetin.',
  },
};

export function getHubCtaForBlog(blogSlug: string | null | undefined) {
  if (!blogSlug) return null;
  return BLOG_HUB_CTAS[blogSlug.toLowerCase()] ?? null;
}

export { BLOG_DNA, MATCH_THRESHOLD, MAX_TOOLS_PER_BLOG };
